/**
 * This module exports Scalyr-defined pure functions for processing logic related to routing, url paths and query
 * string parameters, etc. It makes heavy use of the browser-provided URLSearchParams object.
 */

import { compact } from 'lodash';
import * as H from 'history';
import * as K from 'common/constants';
import { UrlParams } from 'common/types';
import { LogEntry } from 'common/graphqlTypes';
import UrlParamsByRoute from 'common/params';
import { fromEntries } from 'common/util/language';
import { convertTimestampToMilliseconds } from 'common/util/time';
import { HELP_ROUTE } from 'components/help/constants';

export type LocationOrURL = Location | URL | H.Location;

/**
 * Returns the current path, plus the mode if present, in a normalized string.
 * TODO: This function falsely asserts that it always returns a valid {@see ROUTE}
 *
 * @param location    a location object (from window or react-router)
 * @returns {K.ROUTE}
 */
export const getScalyrPath = (location: LocationOrURL) => {
  const path = location.pathname;
  const validRoutes = Object.values(K.ROUTE as Record<string, K.ROUTE>);
  const routeParams = [K.URL_PARAM.MODE, K.URL_PARAM.DASHBOARD_NAME];

  // If the list of valid routes includes the current path and some valid routeParam value, then return that route.
  // For example, '/events?mode=graph' matches a K.ROUTE, so we would return it here, but '/events?mode=log' does not,
  // so it would fall through.
  for (const paramKey of routeParams) {
    const paramValue = new URLSearchParams(location.search).get(paramKey) ?? '';
    const routeWithParam = [path, `${paramKey}=${paramValue}`].join('?') as K.ROUTE;
    if (validRoutes.includes(routeWithParam)) {
      return routeWithParam;
    }
  }

  // otherwise, only the path is included; all params are ignored, including "mode" and "page"
  return path as K.ROUTE;
};

/**
 * Checks whether the route for a given link is registered in the given list of React routes.
 *
 * If the given list of routes has a single member of "*", then we are doing wildcard routing, and every known route that is
 * not in the provided blacklist is a React route.
 *
 * @param  location                a location object (from window or react-router)
 * @param  reactRoutes             list of the registered routes that are whitelisted for navigation within the React SPA
 * @param  reactRoutesBlacklist    list of routes that are blacklisted for navigation within the React SPA
 *
 * @returns               true if the route is registered for service by React
 */
export const isRegisteredRoute = (
  location: LocationOrURL,
  reactRoutes?: Array<string>,
  reactRoutesBlacklist?: Array<string>,
) => {
  if (!reactRoutes) {
    return false;
  }

  let routes = compact([...reactRoutes]);
  // check for wildcard routing
  if (reactRoutes.length === 1 && reactRoutes[0] === '*') {
    routes = Object.values(K.ROUTE);
  }

  const path = location.pathname.toLowerCase();

  routes = routes.map((reactRoute) => reactRoute.trim().toLowerCase());
  const isKnownRoute = routes.includes(path);
  // we don't list all ~150 help routes in reactPaths, so look in HELP_ROUTE
  const isHelpRoute = Object.values(HELP_ROUTE).includes(path as HELP_ROUTE);
  const nonRoutes = compact(reactRoutesBlacklist?.map((nonRoute) => nonRoute.trim().toLowerCase()));
  const isBlacklistedRoute = nonRoutes && nonRoutes.filter((nonRoute) => path.startsWith(nonRoute)).length > 0;

  return (isKnownRoute || isHelpRoute) && !isBlacklistedRoute;
};

/**
 * Return the lists of event, view, and disposable params for the given route.
 *
 * @param  location    a location object (from window or react-router)
 *
 * @return the lists of event, view, and disposable params for the given route.
 */
export const getUrlParamSet = (location: LocationOrURL) => {
  const path = getScalyrPath(location);
  return UrlParamsByRoute[path] || { eventParamList: [], viewParamList: [], disposableParamList: [] };
};

/**
 * Extract a single param from a query string.
 *
 * @param search    query string
 * @param param     param name
 *
 * @return the value of the given param
 */
export const getUrlParam = (search: string, param: string) => {
  const searchParams = new URLSearchParams(search);
  return searchParams.get(param) || '';
};

/**
 * Return the params from a query string as a hash.
 *
 * @param search    query string
 *
 * @return query string params as a key/value hash
 */
export const getUrlParams = (search: string) => fromEntries(new URLSearchParams(search)) as UrlParams;

/**
 * Set a param in a query string.
 *
 * @param search    query string
 * @param param     param name
 * @param value     param value
 *
 * @return the updated query string
 */
export const setUrlParam = (search: string, param: string, value: string) => {
  const searchParams = new URLSearchParams(search);
  searchParams.set(param, value);
  return searchParams.toString();
};

/**
 * Apply a hash of params to a query string.
 *
 * @param search           query string
 * @param newParameters    hash of param keys/values to apply to query string
 *
 * @return the updated query string
 */
export const setUrlParams = (search: string, newParameters: UrlParams) => {
  const searchParams = new URLSearchParams(search);
  const newSearchParams = new URLSearchParams(newParameters);
  Array.from(newSearchParams.entries()).forEach((pair) => {
    const [key, value] = pair;
    searchParams.set(key, value);
  });

  return searchParams.toString();
};

/**
 * Filter a hash of params or a query string based on a whitelist.
 *
 * @param parameters      a query string or parameter hash
 * @param whitelist       list of parameters to include
 *
 * @return a hash of the input, but with params not included in the whitelist removed
 */
export const filterUrlParams = (parameters: UrlParams | string, whitelist: Array<string>) => {
  const searchParams = new URLSearchParams(parameters);
  const filteredParams = new URLSearchParams();
  Array.from(searchParams.keys())
    .filter((param) => whitelist.includes(param))
    .forEach((param) => filteredParams.set(param, searchParams.get(param) || ''));

  return fromEntries(filteredParams.entries()) as UrlParams;
};

/**
 * // TODO-perf need-ticket: causes component re-render on every call due to new return always contains new object
 * //   we need to transition to the `usePureGetWhitelistedUrlParams()`
 *
 * Get the value of each of the whitelisted parameters from a given hash of params or query string.
 *
 * @param parameters      a query string or parameter hash
 * @param whitelist       list of parameters to include in the returned hash
 *
 * @return a hash of whitelisted params
 */
export const getWhitelistedUrlParams = (parameters: UrlParams | string, whitelist: Array<string>) => {
  const searchParams = new URLSearchParams(parameters);
  const filteredParams = new URLSearchParams();
  whitelist.forEach((param) => filteredParams.set(param, searchParams.get(param) || ''));

  return fromEntries(filteredParams.entries()) as UrlParams;
};

/**
 * Get the query string representation of the values of all of the whitelisted parameters from a given hash of params
 * or query string.
 *
 * @param parameters      a query string or parameter hash
 * @param whitelist       list of parameters to include in the returned query string
 *
 * @return a query string
 */
export const getWhitelistedUrlParamsString = (parameters: UrlParams | string, whitelist: Array<string>) =>
  new URLSearchParams(getWhitelistedUrlParams(parameters, whitelist)).toString();

/**
 * Set or remove parameters in one set based on the values in another set.
 *
 * @param parameters       initial set of parameters
 * @param newParameters    list of parameters to set; any with a falsy value will be removed
 *
 * @return a hash of search params
 */
export const updateUrlParams = (parameters: UrlParams | string, newParameters: UrlParams | string) => {
  const searchParams = new URLSearchParams(parameters);
  const newSearchParams = new URLSearchParams(newParameters);
  Array.from(newSearchParams.entries()).forEach((pair) => {
    const [key, value] = pair;
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
  });

  return fromEntries(searchParams.entries()) as UrlParams;
};

/**
 * Set or remove parameters in one set based on the values in another set.
 *
 * @param parameters       initial set of parameters
 * @param newParameters    list of parameters to set; any with a falsy value will be removed
 *
 * @return a query string
 */
export const updateUrlParamsString = (parameters: UrlParams | string, newParameters: UrlParams | string) =>
  new URLSearchParams(updateUrlParams(parameters, newParameters)).toString();

/**
 * Return a relative URL built from the supplied path and query string parameters. The path may have
 * a query string component to it.
 *
 * @param route         URL path
 * @param parameters    query string parameters
 * @param hash          URL hash (including the #)
 *
 * @return a relative URL (starting with the given path)
 */
export const buildUrl = (route: string, parameters?: UrlParams | string, hash = '') => {
  const [path, routeQueryString] = route.split('?');
  const queryString = updateUrlParamsString(routeQueryString || '', parameters || '');
  return queryString ? path + '?' + queryString + hash : path + hash;
};

/**
 * Return an absolute URL given some relative path. The relative path may have url params
 *
 * @param relativePath  URL relative path
 *
 * @return absolute URL
 */
export const buildAbsoluteUrl = (relativePath: string) => new URL(relativePath, window.location.href).toString();

/**
 * Given a log entry, return a link to its parser (if it has one).
 *
 * @param logEntry    log line data
 *
 * @return link to the log line's parser
 */
export const getParserLink = (logEntry: LogEntry) => {
  const parser = logEntry?.serverInfo?.parser;
  return parser ? buildUrl(K.ROUTE.PARSER, { [K.URL_PARAM.PARSER]: parser } as UrlParams) : '';
};

/**
 * Given a log entry, return a link that shows it in its original or thread log context.
 *
 * @param logEntry             log line data
 * @param continuationToken    unique ID for the log line
 * @param timeWindow           time range for centered search, in ms
 * @param threadId             thread ID
 *
 * @return link to the log line's parser
 */
export const getLogLink = (
  logEntry: LogEntry,
  continuationToken: string,
  timeWindow: number,
  now: number,
  threadId?: string,
) => {
  const timestamp = convertTimestampToMilliseconds(logEntry?.timestamp);
  const timeDelta = timeWindow / 2;
  const params = {
    continuationToken,
    centered: true,
    startTime: timestamp - timeDelta,
    endTime: Math.min(timestamp + timeDelta, now),
  } as unknown as UrlParams;

  const serverHost = logEntry?.serverInfo?.serverHost;
  const logfile = logEntry?.serverInfo?.logfile;
  const sessionId = logEntry?.sessionId;
  if ((!serverHost || !logfile) && sessionId) {
    params[K.URL_PARAM.FILTER] = `$session="${sessionId}"`;
  } else {
    // TODO: code assumes these are always special parameters on the Search Page
    params[K.URL_PARAM.LOG_SOURCE] = serverHost;
    params[K.URL_PARAM.LOG] = logfile;
  }

  if (threadId) {
    const term = `threadId="${threadId}"`;
    const filter = params[K.URL_PARAM.FILTER];
    params[K.URL_PARAM.FILTER] = filter ? [filter, term].join(' ') : term;
  }

  return buildUrl(K.ROUTE.EVENTS, params);
};

/**
 * Returns a URL for a given dashboard
 *
 * @param dashboardName dashboard name
 */
export const getDashboardLink = (dashboardName: string) =>
  buildUrl(K.ROUTE.DASHBOARD, { [K.URL_PARAM.DASHBOARD_NAME]: dashboardName } as UrlParams);

/**
 * Remove search parameters and build new URL
 *
 * @param location        current location
 * @param searchParams    list of parameters to remove
 *
 * @return a new URL string
 */
export const removeParamsFromUrl = (location: LocationOrURL, searchParams: Array<K.URL_PARAM>) => {
  const currentSearchParams = new URLSearchParams(location.search);
  searchParams.forEach((searchParam: string) => currentSearchParams.delete(searchParam));
  return buildUrl(location.pathname, currentSearchParams.toString());
};

/**
 * Constructs a URL combining current event params with new ones, ensuring that only params that are relevant for
 * the given route are included.
 *
 * @param route        page route
 * @param curParams    set of existing event params
 * @param newParams    params to add/set in link
 *
 * @return a string link to the given route with its params
 */
export const getLinkForRoute = (route: K.ROUTE, curParams: UrlParams, newParams = {} as UrlParams) => {
  let params = { ...curParams, ...newParams } as UrlParams;
  params = getWhitelistedUrlParams(
    params,
    UrlParamsByRoute[route].eventParamList.concat(UrlParamsByRoute[route].viewParamList),
  );

  return buildUrl(route, params);
};

/**
 * Normalize a link. Remove an incorrectly prepended slash. Turn an absolute URL into a relative URL if it
 * shares the current origin (protocol/domain/port).
 *
 * @param link        link to normalize
 * @param location    current location (for testing)
 * @return a normalized link
 */
export const normalizeLink = (link: string, location: LocationOrURL = window.location) => {
  if (!link) {
    return '';
  }

  let href = link;
  if (href && (href.startsWith('/http:') || href.startsWith('/https:'))) {
    // our server sometimes prepends slash to URLs (eg in reports)
    href = href.substring(1);
  }
  let url;
  try {
    url = new URL(href);
  } catch (e) {
    // ignore
  }
  if (url && 'origin' in location && url.origin === location.origin) {
    href = buildUrl(url.pathname, url.search);
  }

  return href;
};

/**
 * Verify if page needs to clear team token data.
 *
 * @param pathname current pathname.
 */
export const isTeamTokenClearingPage = (pathname: string) => pathname === K.ROUTE.LOG_IN;

/**
 * Validates a URL path against a list of valid routes (ie: '/logs', '/dash?page=1`)
 *
 * @param url URL to validate
 * @param validRoutes array of routes (strings) to validate against
 */
export const isRelativeUrlValidRoute = (pathAndSearch: string, validRoutes: string[]) => {
  if (!pathAndSearch || !pathAndSearch.startsWith('/')) {
    return false;
  }

  const [pathname, search] = pathAndSearch.split('?');
  const searchParams = new URLSearchParams(search);

  for (let i = 0; i < validRoutes.length; i++) {
    const [validPathname, validSearch] = validRoutes[i].split('?');
    if (validPathname === pathname) {
      if (!validSearch) {
        return true;
      }
      const validSearchParams = new URLSearchParams(validSearch);
      const isMatch = Array.from(validSearchParams.entries()).reduce(
        (acc, [key, value]) => acc && searchParams.get(key) === value,
        true,
      );

      if (isMatch) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Returns a 2 element array:
 * First element is a relative URL from the input. Before converting, it tries to validate first if it's already
 * a relative URL and then validates if it's a valid URL. When valid, second return element will be `undefined`.
 * Second element is an error message if cannot convert the URL. When there is an error, first element will be `undefined`.
 * Will return empty string if input is empty, will not return an error.
 *
 * @param value either a valid URL or a relative URL
 */
export const convertUrlToRelative = (value: string): [string, undefined] | [undefined, string] => {
  if (value.trim().length === 0) {
    return ['', undefined];
  }

  if (value.startsWith('/')) {
    return [value, undefined];
  }

  try {
    const { pathname, search } = new URL(value);
    return [`${pathname}${search}`, undefined];
  } catch (err) {
    return [undefined, 'The given URL is not valid'];
  }
};
